import { cn } from "@gymflow/helpers";
import { Field, FieldProps, FormikProps } from "formik";

import { OptionType } from "../../base/components/OptionsList/components/OptionItem";
import {
  SelectFetchingQueryType,
  SelectInput,
  SelectInputProps,
} from "../../base/SelectInput";
import { ErrorMessage } from "../../ErrorMessage";
import { WarningMessage } from "../../WarningMessage";
import { Path } from "../types";

export type FormSelectInputProps<T, V> = {
  name: Path<T>;
  containerClassName?: string;
  warningMessage?: string;
} & (
  | (Omit<SelectInputProps<V>, "value" | "onChange" | "dataFetchingQuery"> & {
      options: OptionType<V>[];
    })
  | (Omit<SelectInputProps<V>, "value" | "onChange" | "options"> & {
      dataFetchingQuery: (search?: string) => SelectFetchingQueryType<V>;
      isSearchable?: boolean;
    })
);

export const FormSelectInput = <T, V>({
  name,
  className,
  containerClassName,
  warningMessage,
  ...props
}: FormSelectInputProps<T, V>) => (
  <Field name={name}>
    {({
      field,
      form: { setFieldValue },
      meta: { touched, error },
    }: FieldProps<OptionType<V>[] | OptionType<V>, FormikProps<T>>) => {
      const isError = touched && error;
      return (
        <div className={cn("flex flex-col gap-1.5", containerClassName)}>
          <SelectInput
            {...props}
            {...(props.isMulti
              ? {
                  isMulti: true,
                  value: (field.value as OptionType<V>[]) ?? [],
                }
              : {
                  isMulti: false,
                  value: field.value as OptionType<V> | undefined,
                })}
            onChange={(selected?: OptionType<V>[] | OptionType<V>) =>
              setFieldValue(String(name), selected)
            }
            className={cn(className, {
              "outline-error-300 border-error-200": isError,
            })}
          />
          {isError && <ErrorMessage text={error} />}
          {warningMessage && <WarningMessage text={warningMessage} />}
        </div>
      );
    }}
  </Field>
);
